import { AppStateErrors } from '../../../learn/constants/errors.ts';
import { AppStateEnum } from '../../../main/types/app-state.types.ts';
import { StepType, TaskType } from '../../../main/types/asset/asset.enums.ts';
import { getSentryUtil, SentryTrackerUtil } from '../../../main/utils/monitoring/sentry.util.ts';
import { getReduxStoreUtil } from '../../../main/utils/redux-store.util.ts';

import { selectCurrentLessonTaskStepKey } from '../../selectors/current-lesson-task-step-key.selector.ts';
import { selectCurrentStepType } from '../../selectors/current-step-type.selector.ts';
import { selectCurrentTaskType } from '../../selectors/current-task-type.selector.ts';

import { getAppAskStateChangeHandlerUtil } from './handlers/app-ask/app-ask.handler.ts';
import { getCapitalLettersAskToTapStateChangeHandlerUtil } from './handlers/app-ask/capital-letters-ask-to-tap.handler.ts';
import { getPictureAskToTapStateChangeHandlerUtil } from './handlers/app-ask/picture-ask-to-tap.handler.ts';
import { getAppPronounceStateChangeHandlerUtil } from './handlers/app-say/app-pronounce.handler.ts';
import { getAppSayStateChangeHandlerUtil } from './handlers/app-say/app-say.handler.ts';
import { getAppSoundOutStateChangeHandlerUtil } from './handlers/app-say/app-sound-out.handler.ts';
import { BaseAppStateChangeHandlerUtil } from './handlers/base-app-state-change.handler.ts';
import { getStoryAppAskStateChangeHandlerUtil } from './handlers/story-reading/story-app-ask.handler.ts';
import { getStoryAppReadSentenceStateChangeHandlerUtil } from './handlers/story-reading/story-app-read-sentence.handler.ts';
import { getStoryAppSayStateChangeHandlerUtil } from './handlers/story-reading/story-app-say.handler.ts';
import { getStoryAskToTapWordStateChangeHandlerUtil } from './handlers/story-reading/story-ask-to-tap-word.handler.ts';
import { getStoryBothReadSentenceStateChangeHandlerUtil } from './handlers/story-reading/story-both-read-sentence.handler.ts';
import { getStoryUserReadSentenceStateChangeHandlerUtil } from './handlers/story-reading/story-user-read-sentence.handler.ts';
import { getStoryUserSayWordStateChangeHandlerUtil } from './handlers/story-reading/story-user-say-word.handler.ts';
import { getUserPronounceStateChangeHandlerUtil } from './handlers/user-say/user-pronounce.handler.ts';
import { getUserSayLetterNameStateChangeHandlerUtil } from './handlers/user-say/user-say-letter-name.handler.ts';
import { getUserSayRhymeSoundStateChangeHandlerUtil } from './handlers/user-say/user-say-rhyme-sound.handler.ts';
import { getUserSayWordStateChangeHandlerUtil } from './handlers/user-say/user-say-word.handler.ts';
import { getAppWriteStateChangeHandlerUtil } from './handlers/writing/app-write.handler.ts';
import { getUserWriteStateChangeHandlerUtil } from './handlers/writing/user-write.handler.ts';

export class AppStateUtil {
  private static _instance: AppStateUtil;

  public static get instance(): AppStateUtil {
    if (!AppStateUtil._instance) {
      AppStateUtil._instance = new AppStateUtil();
    }
    return AppStateUtil._instance;
  }

  private taskStepHandlerMap: Map<TaskType, Map<StepType, BaseAppStateChangeHandlerUtil>>;

  private constructor(
    private readonly storyUserReadSentenceHandler: BaseAppStateChangeHandlerUtil = getStoryUserReadSentenceStateChangeHandlerUtil(),
    private readonly storyAppReadSentenceHandler: BaseAppStateChangeHandlerUtil = getStoryAppReadSentenceStateChangeHandlerUtil(),
    private readonly storyAppAskHandler: BaseAppStateChangeHandlerUtil = getStoryAppAskStateChangeHandlerUtil(),
    private readonly storyUserSayWordHandler: BaseAppStateChangeHandlerUtil = getStoryUserSayWordStateChangeHandlerUtil(),
    private readonly storyAppSayHandler: BaseAppStateChangeHandlerUtil = getStoryAppSayStateChangeHandlerUtil(),
    private readonly storyBothReadSentenceHandler: BaseAppStateChangeHandlerUtil = getStoryBothReadSentenceStateChangeHandlerUtil(),
    private readonly storyAppAskToTapWordHandler: BaseAppStateChangeHandlerUtil = getStoryAskToTapWordStateChangeHandlerUtil(),
    private readonly userPronounceHandler: BaseAppStateChangeHandlerUtil = getUserPronounceStateChangeHandlerUtil(),
    private readonly userSayLetterNameHandler: BaseAppStateChangeHandlerUtil = getUserSayLetterNameStateChangeHandlerUtil(),
    private readonly userSayWordHandler: BaseAppStateChangeHandlerUtil = getUserSayWordStateChangeHandlerUtil(),
    private readonly userSayRhymeSoundHandler: BaseAppStateChangeHandlerUtil = getUserSayRhymeSoundStateChangeHandlerUtil(),
    private readonly appSayHandler: BaseAppStateChangeHandlerUtil = getAppSayStateChangeHandlerUtil(),
    private readonly appSoundOutHandler: BaseAppStateChangeHandlerUtil = getAppSoundOutStateChangeHandlerUtil(),
    private readonly appAskHandler: BaseAppStateChangeHandlerUtil = getAppAskStateChangeHandlerUtil(),
    private readonly capitalLettersAskToTapHandler: BaseAppStateChangeHandlerUtil = getCapitalLettersAskToTapStateChangeHandlerUtil(),
    private readonly pictureAskToTapHandler: BaseAppStateChangeHandlerUtil = getPictureAskToTapStateChangeHandlerUtil(),
    private readonly appPronounceHandler: BaseAppStateChangeHandlerUtil = getAppPronounceStateChangeHandlerUtil(),
    private readonly appWriteHandler: BaseAppStateChangeHandlerUtil = getAppWriteStateChangeHandlerUtil(),
    private readonly userWriteHandler: BaseAppStateChangeHandlerUtil = getUserWriteStateChangeHandlerUtil(),
    private readonly sentry: SentryTrackerUtil = getSentryUtil()
  ) {
    this.taskStepHandlerMap = this.initializeTaskStepHandlerMap();
  }

  private initializeTaskStepHandlerMap(): Map<TaskType, Map<StepType, BaseAppStateChangeHandlerUtil>> {
    return new Map<TaskType, Map<StepType, BaseAppStateChangeHandlerUtil>>([
      [
        TaskType.FirstReading,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserReadSentence, this.storyUserReadSentenceHandler],
          [StepType.AppReadSentence, this.storyAppReadSentenceHandler],
          [StepType.AppAsk, this.storyAppAskHandler],
          [StepType.UserSoundOut, this.storyUserSayWordHandler],
          [StepType.UserSayFast, this.storyUserSayWordHandler],
          [StepType.UserSoundOutContinuous, this.storyUserSayWordHandler],
        ]),
      ],
      [
        TaskType.SecondReading,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserReadSentence, this.storyUserReadSentenceHandler],
          [StepType.AppAsk, this.storyAppAskHandler],
          [StepType.UserSoundOut, this.storyUserSayWordHandler],
          [StepType.AppSay, this.storyAppSayHandler],
        ]),
      ],
      [
        TaskType.CritterReadsTheFastWay,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.AppReadSentence, this.storyAppReadSentenceHandler],
        ]),
      ],
      [
        TaskType.ReadingTheFastWay,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.AppReadSentence, this.storyAppReadSentenceHandler],
          [StepType.UserReadSentence, this.storyUserReadSentenceHandler],
          [StepType.BothReadSentence, this.storyBothReadSentenceHandler],
        ]),
      ],
      [
        TaskType.WordFinding,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.AppAskToTap, this.storyAppAskToTapWordHandler],
          [StepType.AppSay, this.storyAppSayHandler],
        ]),
      ],
      [
        TaskType.SoundsIntroduction,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserPronounce, this.userPronounceHandler],
          [StepType.AppPronounce, this.appPronounceHandler],
        ]),
      ],
      [
        TaskType.Sounds,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserPronounce, this.userPronounceHandler],
          [StepType.UserSayLetterName, this.userSayLetterNameHandler],
          [StepType.AppPronounce, this.appPronounceHandler],
        ]),
      ],
      [
        TaskType.SoundsReview,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserPronounce, this.userPronounceHandler],
          [StepType.AppPronounce, this.appPronounceHandler],
        ]),
      ],
      [
        TaskType.ChildTouchesSounds,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserPronounce, this.userPronounceHandler],
          [StepType.UserPronounceFast, this.userPronounceHandler],
        ]),
      ],
      [
        TaskType.Rhyming,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserPronounce, this.userPronounceHandler],
          [StepType.UserSayRhymeSound, this.userSayRhymeSoundHandler],
          [StepType.UserSoundOut, this.userSayWordHandler],
          [StepType.AppSoundOut, this.appSoundOutHandler],
        ]),
      ],
      [
        TaskType.SoundsWriting,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserPronounce, this.userPronounceHandler],
          [StepType.UserSayLetterName, this.userSayLetterNameHandler],
          [StepType.AppWrite, this.appWriteHandler],
          [StepType.UserWrite, this.userWriteHandler],
        ]),
      ],
      [
        TaskType.CapitalLetters,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserSayLetterName, this.userSayLetterNameHandler],
          [StepType.AppAskToTap, this.capitalLettersAskToTapHandler],
        ]),
      ],
      [
        TaskType.SayItFast,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserSayFast, this.userSayWordHandler],
          [StepType.AppSayFast, this.appSayHandler],
        ]),
      ],
      [
        TaskType.SayTheSounds,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserSayFast, this.userSayWordHandler],
          [StepType.AppSay, this.appSayHandler],
          [StepType.AppSayFast, this.appSayHandler],
        ]),
      ],
      [
        TaskType.SayItFastReview,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([[StepType.UserSayFast, this.userSayWordHandler]]),
      ],
      [
        TaskType.WordReading,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserSayFast, this.userSayWordHandler],
          [StepType.UserSoundOut, this.userSayWordHandler],
          [StepType.AppSay, this.appSayHandler],
          [StepType.AppSoundOut, this.appSoundOutHandler],
        ]),
      ],
      [
        TaskType.SayTheSoundsReview,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.UserSayFast, this.userSayWordHandler],
          [StepType.UserSoundOut, this.userSayWordHandler],
          [StepType.AppSay, this.appSayHandler],
          [StepType.AppSoundOut, this.appSoundOutHandler],
        ]),
      ],
      [
        TaskType.PictureComprehension,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([
          [StepType.AppSay, this.appSayHandler],
          [StepType.AppAsk, this.appAskHandler],
          [StepType.AppAskToTap, this.pictureAskToTapHandler],
        ]),
      ],
      [TaskType.NewCritter, new Map<StepType, BaseAppStateChangeHandlerUtil>([[StepType.AppAsk, this.appAskHandler]])],
      [
        TaskType.LessonComplete,
        new Map<StepType, BaseAppStateChangeHandlerUtil>([[StepType.AppSay, this.appSayHandler]]),
      ],
    ]);
  }

  private async getHandler(): Promise<BaseAppStateChangeHandlerUtil | undefined> {
    const rootState = await getReduxStoreUtil().getRootState();
    const taskType = selectCurrentTaskType(rootState);
    const stepType = selectCurrentStepType(rootState);
    if (!taskType || !stepType) return undefined;

    // Try to get step-specific handler
    const stepHandlers = this.taskStepHandlerMap.get(taskType);
    if (stepHandlers) {
      const stepHandler = stepHandlers.get(stepType);
      if (stepHandler) {
        return stepHandler;
      }
    }

    const key = selectCurrentLessonTaskStepKey(rootState);
    // If no handler is found, track an error
    this.sentry.trackError(new Error(AppStateErrors.NO_HANDLER_FOUND), {
      taskType,
      stepType,
      lesson: key.lesson,
      task: key.task,
      step: key.step,
    });
  }

  public async handleAppStateChange(state: AppStateEnum): Promise<void> {
    const handler = await this.getHandler();
    handler?.handleAppStateChange(state);
  }
}

export const getAppStateUtil = (): AppStateUtil => AppStateUtil.instance;
